<template>
  <section data-testid="inventory-sync-section-panel">
    <!-- Reconciled funnel not captured for this run. -->
    <v-alert
      v-if="!vm.hasFunnel"
      type="info"
      variant="tonal"
      density="comfortable"
      data-testid="inventory-sync-not-captured"
    >
      {{ vm.notCapturedNote }}
    </v-alert>

    <template v-else>
      <!-- Funnel breakdown: intake -> continued / held back / (admitted). -->
      <div class="d-flex flex-column ga-1" data-testid="inventory-sync-funnel">
        <div class="text-body-2 font-weight-medium" data-testid="inventory-sync-intake">
          {{ vm.headline }}
        </div>
        <div
          v-for="line in vm.funnelLines"
          :key="line.id"
          class="text-body-2 inventory-sync-funnel-line"
          :data-testid="`inventory-sync-funnel-line-${line.id}`"
        >
          {{ line.label }}
        </div>
      </div>

      <!-- Held-back reason breakdown — only when multiple distinct reasons exist. -->
      <div
        v-if="vm.showHeldBackReasonBreakdown"
        class="mt-3"
        data-testid="inventory-sync-reason-buckets"
      >
        <div class="d-flex flex-column ga-1">
          <div
            v-for="bucket in vm.heldBackReasonBuckets"
            :key="bucket.code"
            class="text-body-2 d-flex align-center ga-2"
            :data-testid="`inventory-sync-reason-bucket-${bucket.code}`"
          >
            <v-chip size="x-small" variant="tonal" color="warning">{{ bucket.count }}</v-chip>
            <span>{{ bucket.label }}</span>
          </div>
        </div>
      </div>

    </template>

    <!-- Drill-down dialog: paged records for the selected action bucket. -->
    <v-dialog
      v-model="dialogOpen"
      max-width="760"
      scrollable
      data-testid="inventory-sync-drilldown-dialog"
    >
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between ga-2">
          <span class="text-subtitle-1 font-weight-bold">Inventory Sync</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            aria-label="Close"
            data-testid="inventory-sync-drilldown-close"
            @click="dialogOpen = false"
          />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div
            v-if="browseStatus === 'loading' && records.length === 0"
            class="d-flex align-center ga-2"
            data-testid="inventory-sync-drilldown-loading"
          >
            <v-progress-circular indeterminate color="primary" size="20" />
            <span class="text-body-2">Loading VINs...</span>
          </div>

          <v-alert
            v-else-if="browseStatus === 'unavailable' && records.length === 0"
            type="info"
            variant="tonal"
            density="comfortable"
            data-testid="inventory-sync-drilldown-unavailable"
          >
            We couldn't load these VINs right now. Please try again.
          </v-alert>

          <template v-else-if="records.length > 0">
            <div class="text-caption text-medium-emphasis mb-2" data-testid="inventory-sync-drilldown-heading">
              {{ drillDownHeading }}
            </div>
            <div
              v-if="activeBucket === 'held_back'"
              class="text-caption text-medium-emphasis mb-3"
              data-testid="inventory-sync-drilldown-helper"
            >
              These VINs returned in the feed but were not restored because they still match blocking Publish Rules.
            </div>
            <div class="inventory-sync-results-scroll">
              <v-table density="compact" class="border rounded" data-testid="inventory-sync-drilldown-table">
                <thead>
                  <tr>
                    <th>VIN</th>
                    <th>Reason</th>
                    <th>Source feeds</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in rows"
                    :key="row.key"
                    :data-testid="`inventory-sync-drilldown-row-${row.vin}`"
                  >
                    <td><code class="vin-cell">{{ row.vin }}</code></td>
                    <td>
                      <template v-if="row.rule.kind === 'captured'">
                        <div
                          class="text-caption"
                          :data-testid="`inventory-sync-drilldown-rule-${row.vin}`"
                        >
                          Rule: {{ row.rule.label }}
                        </div>
                        <div
                          v-if="row.rule.actualValueSummary"
                          class="text-caption text-medium-emphasis"
                        >
                          Vehicle: {{ row.rule.actualValueSummary }}
                        </div>
                      </template>
                      <div
                        v-else-if="row.rule.kind === 'notCaptured'"
                        class="text-caption text-medium-emphasis font-italic mt-1"
                        :data-testid="`inventory-sync-drilldown-rule-${row.vin}`"
                      >
                        {{ row.rule.label }}
                      </div>
                      <div
                        v-if="row.anomalyDetail"
                        class="text-caption text-warning"
                        :data-testid="`inventory-sync-drilldown-anomaly-${row.vin}`"
                      >
                        {{ row.anomalyDetail }}
                      </div>
                    </td>
                    <td>{{ row.feedSourcesLine }}</td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <div class="d-flex align-center justify-center mt-3">
              <v-btn
                v-if="hasMore"
                size="small"
                variant="tonal"
                color="primary"
                :loading="browseStatus === 'loading'"
                :disabled="browseStatus === 'loading'"
                data-testid="inventory-sync-drilldown-load-more"
                @click="loadMore"
              >
                Load more
              </v-btn>
            </div>
          </template>

          <v-alert
            v-else
            type="info"
            variant="tonal"
            density="comfortable"
            data-testid="inventory-sync-drilldown-empty"
          >
            No VINs in this bucket for this run.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  tryGetPublishFlowInventorySyncDecisions,
  type PublishFlowInventorySyncBucket,
  type PublishFlowInventorySyncDecisionRecord,
} from '@/services/publishFlowDecisionService'
import {
  buildInventorySyncCardViewModel,
  describeInventorySyncRule,
  type InventorySyncDrillDownBucket,
} from '@/utils/publishFlowBusinessSections'
import type { PublishFlowStageDetailFunnel } from '@/services/publishFlowObservabilityService'
import { formatFeedSource } from '@/utils/feedSourceFormat'

/**
 * Inventory Sync expanded-section panel. Self-contained so the large
 * detailed-impact dialog stays focused: it renders the reconciled funnel
 * breakdown + held-back reason buckets, and owns its own paged drill-down
 * dialog over `stage-decisions/inventorySync?action=<bucket>`.
 *
 * The reconciled funnel slice (`details.funnel`) is the AUTHORITATIVE source
 * for all counts — this panel never recomputes them from the records.
 */
const props = defineProps<{
  funnel: PublishFlowStageDetailFunnel | null
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
}>()

const PAGE_SIZE = 50

const vm = computed(() => buildInventorySyncCardViewModel(props.funnel))

type BrowseStatus = 'idle' | 'loading' | 'ok' | 'unavailable'

const dialogOpen = ref(false)
const activeBucket = ref<InventorySyncDrillDownBucket | null>(null)
const records = ref<PublishFlowInventorySyncDecisionRecord[]>([])
const totalRecords = ref(0)
const nextCursor = ref<string | undefined>(undefined)
const browseStatus = ref<BrowseStatus>('idle')

const hasMore = computed(
  () => typeof nextCursor.value === 'string' && nextCursor.value.length > 0,
)

const drillDownHeading = computed(() =>
  activeBucket.value === 'held_back'
    ? `Showing ${records.value.length} of ${totalRecords.value} held-back VINs`
    : `Showing ${records.value.length} of ${totalRecords.value} ${
        totalRecords.value === 1 ? 'VIN' : 'VINs'
      }`,
)

function formatFeedSourcesLine(feedSources: string[] | undefined): string {
  if (!Array.isArray(feedSources)) return ''
  const feeds = feedSources
    .map((feed) => feed.trim())
    .filter((feed) => feed.length > 0)
  return Array.from(new Set(feeds)).map(formatFeedSource).join(', ')
}

function anomalyDetailLine(record: PublishFlowInventorySyncDecisionRecord): string {
  if (!record.anomaly) return ''
  return record.anomaly.detail?.trim() || 'Reconciliation conflict — kept out of funnel math.'
}

const rows = computed(() =>
  records.value.map((record, idx) => ({
    key: `${record.vin}:${idx}`,
    vin: record.vin,
    feedSourcesLine: formatFeedSourcesLine(record.provenance?.feedSources),
    anomalyDetail: anomalyDetailLine(record),
    rule: describeInventorySyncRule(record.provenance),
  })),
)

async function fetchPage(cursor?: string) {
  if (!activeBucket.value) return
  browseStatus.value = 'loading'
  const result = await tryGetPublishFlowInventorySyncDecisions({
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    sessionToken: props.sessionToken,
    pageSize: PAGE_SIZE,
    action: activeBucket.value as PublishFlowInventorySyncBucket,
    ...(cursor ? { cursor } : {}),
  })
  if (result.status === 'ok') {
    const page = Array.isArray(result.data.records) ? result.data.records : []
    records.value = cursor ? [...records.value, ...page] : page
    totalRecords.value = result.data.totalRecords ?? records.value.length
    nextCursor.value = result.data.nextCursor
    browseStatus.value = 'ok'
    return
  }
  browseStatus.value = 'unavailable'
}

function openDrillDown(bucket: InventorySyncDrillDownBucket) {
  activeBucket.value = bucket
  records.value = []
  totalRecords.value = 0
  nextCursor.value = undefined
  browseStatus.value = 'idle'
  dialogOpen.value = true
  void fetchPage()
}

function openHeldBackDrillDown() {
  openDrillDown('held_back')
}

function loadMore() {
  if (browseStatus.value === 'loading') return
  const cursor = nextCursor.value
  if (typeof cursor !== 'string' || cursor.length === 0) return
  void fetchPage(cursor)
}

defineExpose({ openHeldBackDrillDown })
</script>

<style scoped>
.inventory-sync-results-scroll {
  max-height: 420px;
  overflow-y: auto;
  border-radius: 4px;
}
.vin-cell {
  font-family: 'JetBrains Mono', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  font-size: 12px;
}
</style>
